import { useState, useEffect, lazy, Suspense } from 'react';
import { HashRouter as Router, Routes, Route } from 'react-router-dom';
import { Elements } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import {
  constants,
  axios,
  Notification,
  notify,
  AppLayout,
  PrivateRoute,
  ActiveRoute,
  PublicRoute,
  SignUpRoute,
  UpdatePassword,
  ForgotPassword,
  Loader,
  ErrorBoundary,
  NotFoundPage,
  SignedOutDialog,
  useAuth,
} from '@bento-dental/core';
import { StorageProvider } from '@bento-dental/core/hooks';
import './index.css';
import EmployerConstants from './constants/constants';
import EmployeeDetails from './components/Dashboard/Employees/EmployeeDetails';
import { CheckUserSignupResponse } from './types';
import type { SignupStepKey } from './components/SignUp/types';

const SignIn = lazy(() => import('./components/SignIn'));
const SignUp = lazy(() => import('./components/SignUp/SignUp'));
const Header = lazy(() => import('./components/Header/Header'));
const EmployerDashboard = lazy(() => import('./components/Dashboard/EmployerDashboard'));
const Employees = lazy(() => import('./components/Dashboard/Employees/Employees'));
const PlanYears = lazy(() => import('./components/Dashboard/PlanYears/PlanYears'));
const Reports = lazy(() => import('./components/Dashboard/Reports/Reports'));
const Account = lazy(() => import('./components/Dashboard/Account/Account'));
const Users = lazy(() => import('./components/Users/Users'));
const RetiredVerificationLink = lazy(() => import('./components/RetiredVerificationLink'));
const CompleteSignup = lazy(() => import('./components/Users/CompleteSignup'));
const CompleteEmailChange = lazy(() => import('./components/Users/CompleteEmailChange'));
const CompleteAccessGrant = lazy(() => import('./components/Users/CompleteAccessGrant'));
const Subscriptions = lazy(() => import('./components/Dashboard/Receipts/Subscriptions'));
const ClaimInvoices = lazy(() => import('./components/ClaimsInvoices/ClaimInvoices'));
const EdiTest = lazy(() => import('./components/EdiTest/EditTest'));

function App() {
  const { isLoggedIn, initializer } = useAuth();
  // Only employer portal uses internal shouldRender state, other portals use the one from useAuth/AuthContext
  const [shouldRender, setShouldRender] = useState(true);
  const [verificationText, setVerificationText] = useState('');
  const [currentUser, setCurrentUser] = useState<CheckUserSignupResponse | null>(null);
  const [isSignUpCompleted, setIsSignUpCompleted] = useState<boolean | null>(null);

  function checkUserSignUp(blockRender = true) {
    setShouldRender(!blockRender);
    return axios
      .get('corp/check-user-signup/')
      .then((response) => {
        setCurrentUser(response.data);
        console.log(response.data);
        setIsSignUpCompleted(
          response.data.current_sign_up_step === EmployerConstants.SIGNUP_STEP_DONE
        );
      })
      .catch((error) => {
        notify(error.message, 'error');
      })
      .finally(() => {
        setShouldRender(true);
      });
  }

  useEffect(() => {
    if (isLoggedIn) {
      checkUserSignUp();
    }
  }, [isLoggedIn]);

  const navLinks = [
    { title: 'Dashboard', id: 'route-dashboard', route: '/dashboard' },
    { title: 'My Employees', id: 'route-employees', route: ['/employees', '/'] },
    currentUser?.batch_payment_invoice_frequency !== 'none'
      ? { title: 'Claims invoices', id: 'route-invoices', route: '/invoices' }
      : null,
    { title: 'Get Reports', id: 'route-reports', route: '/reports' },
    { title: 'View/Edit Plans', id: 'route-plans', route: '/plans' },
    { title: 'Employer Account', id: 'route-account', route: '/account' },
    { title: 'Users', id: 'users', route: '/users' },
  ].filter((link) => link !== null) as Array<{
    title: string;
    id: string;
    route: string | string[];
  }>;

  const hideLayout = !shouldRender || isSignUpCompleted === false || !isLoggedIn;

  const envProps = {
    environment: initializer?.environment,
    version: initializer?.version,
    serverDateTime: initializer?.serverDateTime,
    debug: initializer?.debug,
  };

  return (
    <StorageProvider
      value={{
        isSignUpCompleted,
        shouldRender,
        employerIdFormatted: currentUser?.employer_id_formatted,
        employerName: currentUser?.employer_name,
        userFullName: currentUser?.user_full_name,
        bankStatus: currentUser?.bank_status,
        isEdi: currentUser?.enrollment_type === 'edi',
        accountHolderType: currentUser?.account_holder_type,
        userIsAccountOwner: currentUser?.user_is_account_owner,
        corpId: currentUser?.corp_id,
      }}
    >
      <Elements stripe={initializer?.stripe as Stripe | null}>
        <Suspense fallback={<Loader />}>
          <ErrorBoundary>
            <Router>
              <AppLayout
                links={navLinks}
                envProps={envProps}
                header={
                  <Header
                    refreshSignup={() => {
                      checkUserSignUp(false);
                    }}
                  />
                }
                hideLayout={hideLayout}
              >
                {shouldRender ? (
                  <Suspense fallback={<Loader />}>
                    <Routes>
                      <Route element={<SignUpRoute />}>
                        <Route
                          path={'/signup/*'}
                          element={
                            <SignUp
                              refresh={checkUserSignUp}
                              isAuthenticated={isLoggedIn}
                              currentStep={
                                currentUser?.current_sign_up_step ??
                                (EmployerConstants.SIGNUP_STEP_COMPANY_DETAILS as SignupStepKey)
                              }
                            />
                          }
                        />
                        <Route
                          path={'/sign-up/*'}
                          element={
                            <SignUp
                              refresh={checkUserSignUp}
                              isAuthenticated={isLoggedIn}
                              currentStep={
                                currentUser?.current_sign_up_step ??
                                (EmployerConstants.SIGNUP_STEP_COMPANY_DETAILS as SignupStepKey)
                              }
                            />
                          }
                        />
                      </Route>
                      {/* active routes */}
                      <Route element={<ActiveRoute />}>
                        <Route path="/" element={<Employees />} />
                        <Route path="/dashboard" element={<EmployerDashboard />} />
                        <Route path="/dashboard/:corp" element={<EmployerDashboard />} />
                        <Route path="/employees" element={<Employees />} />
                        <Route path="/reports" element={<Reports />} />
                        <Route path="/account" element={<Account />} />
                        <Route path="/users" element={<Users />} />
                        <Route path="/subscriptions" element={<Subscriptions />} />
                      </Route>
                      {/* private routes */}
                      <Route element={<PrivateRoute />}>
                        <Route path="/invoices" element={<ClaimInvoices />} />
                        <Route path="/employees/:employeeId" element={<EmployeeDetails />} />
                        <Route path="/plans" element={<PlanYears />} />
                      </Route>
                      {/* public routes */}
                      <Route element={<PublicRoute />}>
                        <Route path="/editest" element={<EdiTest />} />
                        <Route
                          path="/sign-in"
                          element={<SignIn verificationText={verificationText} />}
                        />
                        <Route
                          path="/signin"
                          element={<SignIn verificationText={verificationText} />}
                        />
                        <Route
                          path="/forgot-password"
                          element={<ForgotPassword portal={constants.PORTALS.EMPLOYER} />}
                        />
                      </Route>
                      {/* rest routes */}
                      <Route
                        path="/reset-password"
                        element={<UpdatePassword portal={constants.PORTALS.EMPLOYER} />}
                      />
                      <Route path="/complete-signup" element={<CompleteSignup />} />
                      <Route
                        path="/verify-email"
                        element={
                          <RetiredVerificationLink accountType={constants.PORTALS.EMPLOYER} />
                        }
                      />
                      <Route
                        path="/user-sign-up"
                        element={
                          <RetiredVerificationLink accountType={constants.PORTALS.EMPLOYER} />
                        }
                      />
                      <Route path="/complete-email-change" element={<CompleteEmailChange />} />
                      <Route
                        path="/complete-access-grant"
                        element={<CompleteAccessGrant isLoggedIn={isLoggedIn} />}
                      />
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </Suspense>
                ) : (
                  <Loader />
                )}
              </AppLayout>
            </Router>
          </ErrorBoundary>
        </Suspense>
        <SignedOutDialog portal={constants.PORTALS.EMPLOYER} />
        <Notification />
      </Elements>
    </StorageProvider>
  );
}

export default App;
